import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Sparkles,
  Trash2,
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from "lucide-react";
import { ScopePhotosUploader, type ScopePhoto } from "@/components/ScopePhotosUploader";

export const Route = createFileRoute("/_app/estimates/new")({
  component: NewEstimatePage,
});

type LineItem = {
  description: string;
  quantity: number;
  unit_price: number;
};

type ScopeType = "scope" | "out_of_scope" | "scope_change";
type ScopeBlock = {
  id: string;
  type: ScopeType;
  content: string;
};

const SCOPE_OPTIONS: { value: ScopeType; icon: typeof CheckCircle2; color: string }[] = [
  { value: "scope", icon: CheckCircle2, color: "text-emerald-600" },
  { value: "out_of_scope", icon: XCircle, color: "text-destructive" },
  { value: "scope_change", icon: RefreshCw, color: "text-amber-600" },
];

const SCOPE_LABEL_KEYS: Record<ScopeType, string> = {
  scope: "pages.estimates.form.scope",
  out_of_scope: "pages.estimates.form.outOfScope",
  scope_change: "pages.estimates.form.scopeChange",
};

const SCOPE_PLACEHOLDER_KEYS: Record<ScopeType, string> = {
  scope: "pages.estimates.form.scopePlaceholder",
  out_of_scope: "pages.estimates.form.outOfScopePlaceholder",
  scope_change: "pages.estimates.form.scopeChangePlaceholder",
};

type ClientOpt = { id: string; name: string };

function NewEstimatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile, company } = useAuth();
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [clientId, setClientId] = useState<string>("");
  const [title, setTitle] = useState("");

  const [scopeBlocks, setScopeBlocks] = useState<ScopeBlock[]>([
    { id: crypto.randomUUID(), type: "scope", content: "" },
  ]);
  const [aiPrompt, setAiPrompt] = useState("");
  const [items, setItems] = useState<LineItem[]>([{ description: "", quantity: 1, unit_price: 0 }]);
  const [taxPercent, setTaxPercent] = useState<number>(0);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scopePhotos, setScopePhotos] = useState<ScopePhoto[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("clients")
        .select("id, name")
        .eq("is_active", true)
        .order("name");
      setClients((data ?? []) as ClientOpt[]);
    })();
  }, []);

  useEffect(() => {
    if (company?.tax_percent != null) setTaxPercent(Number(company.tax_percent));
  }, [company?.tax_percent]);

  const subtotal = items.reduce(
    (s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
    0,
  );
  const taxAmount = (subtotal * (taxPercent || 0)) / 100;
  const total = subtotal + taxAmount;
  const currency = company?.currency || "USD";

  const formatMoney = (v: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format(v || 0);

  const updateItem = (idx: number, patch: Partial<LineItem>) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addItem = () => setItems((p) => [...p, { description: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (idx: number) =>
    setItems((p) => (p.length === 1 ? p : p.filter((_, i) => i !== idx)));

  const generateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error(t("pages.estimates.form.errorDescribe"));
      return;
    }
    setGenerating(true);
    try {
      const clientName = clients.find((c) => c.id === clientId)?.name;
      const { data, error } = await supabase.functions.invoke("generate-estimate-items", {
        body: {
          description: aiPrompt,
          currency,
          context: clientName ? `For client: ${clientName}` : undefined,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.title && !title) setTitle(data.title);
      if (data?.summary) {
        const summary = String(data.summary);
        setScopeBlocks((prev) => {
          const emptyScopeIdx = prev.findIndex((b) => b.type === "scope" && !b.content.trim());
          if (emptyScopeIdx >= 0) {
            return prev.map((b, i) => (i === emptyScopeIdx ? { ...b, content: summary } : b));
          }
          return [...prev, { id: crypto.randomUUID(), type: "scope", content: summary }];
        });
      }
      if (Array.isArray(data?.items) && data.items.length) {
        setItems(
          data.items.map((it: LineItem) => ({
            description: String(it.description || ""),
            quantity: Number(it.quantity) || 1,
            unit_price: Number(it.unit_price) || 0,
          })),
        );
      }
      toast.success(t("pages.estimates.form.aiReady"));
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to generate";
      toast.error(t("pages.estimates.form.aiFailed"), { description: msg });
    } finally {
      setGenerating(false);
    }
  };

  const save = async (status: "draft" | "sent") => {
    if (!clientId) return toast.error(t("pages.estimates.form.errorClient"));
    if (!title.trim()) return toast.error(t("pages.estimates.form.errorTitle"));
    const cleanItems = items.filter((i) => i.description.trim());
    if (cleanItems.length === 0) return toast.error(t("pages.estimates.form.errorItem"));
    if (!company?.id || !profile?.id) return toast.error(t("pages.estimates.form.errorContext"));

    setSaving(true);
    try {
      const sections: string[] = [];

      for (const block of scopeBlocks) {
        const content = block.content.trim();
        if (!content) continue;
        const heading = t(SCOPE_LABEL_KEYS[block.type]);
        sections.push(`## ${heading}\n${content}`);
      }
      const fullDescription = sections.join("\n\n");

      // Default expiry: 30 days after the issue date (which defaults to today
      // on the database). We compute it here so admins do not need to set it
      // manually for every estimate.
      const today = new Date();
      const expiry = new Date(today);
      expiry.setDate(expiry.getDate() + 30);
      const expiryDate = expiry.toISOString().slice(0, 10);

      // Move draft photos into the estimate folder
      const finalPhotos: { path: string; caption?: string }[] = scopePhotos
        .filter((p) => p.path && !p.uploading)
        .map((p) => ({
          path: p.path,
          ...(p.caption?.trim() ? { caption: p.caption.trim() } : {}),
        }));

      const { data: est, error: eErr } = await supabase
        .from("estimates")
        .insert({
          company_id: company.id,
          client_id: clientId,
          created_by: profile.id,
          title: title.trim(),
          description: fullDescription || null,
          status,
          tax_percent: taxPercent,
          currency,
          number: "",
          expiry_date: expiryDate,
          scope_photos: finalPhotos,
        })
        .select("id")
        .single();
      if (eErr || !est) throw eErr ?? new Error("Failed to create estimate");

      const { error: iErr } = await supabase.from("estimate_items").insert(
        cleanItems.map((it, idx) => ({
          estimate_id: est.id,
          description: it.description.trim(),
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          sort_order: idx,
        })),
      );
      if (iErr) throw iErr;

      toast.success(
        status === "draft"
          ? t("pages.estimates.form.draftSaved")
          : t("pages.estimates.form.estimateSaved"),
      );
      navigate({ to: "/estimates/$estimateId", params: { estimateId: est.id } });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not save";
      toast.error(t("pages.estimates.form.saveFailed"), { description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/estimates" })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("pages.estimates.form.title")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("pages.estimates.form.subtitle")}</p>
        </div>
      </div>

      {clients.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center">
          <h2 className="text-lg font-semibold">{t("pages.estimates.form.noClientsTitle")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("pages.estimates.form.noClientsDesc")}
          </p>
          <Button asChild className="mt-4">
            <Link to="/clients">{t("pages.estimates.form.registerClient")}</Link>
          </Button>
        </div>
      ) : (
        <>
          {/* AI block */}
          <div
            className="rounded-lg border border-border p-5"
            style={{ background: "var(--gradient-subtle, var(--card))" }}
          >
            <div className="mb-3 flex items-center gap-2">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-md text-primary-foreground"
                style={{ background: "var(--gradient-brand)" }}
              >
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold">{t("pages.estimates.form.aiTitle")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("pages.estimates.form.aiSubtitle")}
                </p>
              </div>
            </div>
            <Textarea
              rows={3}
              placeholder={t("pages.estimates.form.aiPlaceholder")}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className="bg-background"
            />
            <div className="mt-3 flex justify-end">
              <Button onClick={generateWithAI} disabled={generating} className="gap-2">
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {generating
                  ? t("pages.estimates.form.generating")
                  : t("pages.estimates.form.generate")}
              </Button>
            </div>
          </div>

          {/* Estimate fields */}
          <div className="grid gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("pages.estimates.form.client")}</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.estimates.form.selectClient")} />
                </SelectTrigger>
                <SelectContent>
                  {clients.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      {t("pages.estimates.form.noClients")}
                    </div>
                  ) : (
                    clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.estimates.form.estimateTitle")}</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("pages.estimates.form.titlePlaceholder")}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>{t("pages.estimates.form.scopeSection")}</Label>
              <p className="text-xs text-muted-foreground">{t("pages.estimates.form.scopeHint")}</p>

              {(() => {
                const block = scopeBlocks[0] ?? {
                  id: "single",
                  type: "scope" as ScopeType,
                  content: "",
                };
                const opt = SCOPE_OPTIONS.find((o) => o.value === block.type) ?? SCOPE_OPTIONS[0];
                const Icon = opt.icon;
                return (
                  <div className="rounded-md border border-border bg-background p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${opt.color}`} />
                      <Select
                        value={block.type}
                        onValueChange={(v) => setScopeBlocks([{ ...block, type: v as ScopeType }])}
                      >
                        <SelectTrigger className="h-8 w-[220px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SCOPE_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {t(SCOPE_LABEL_KEYS[o.value])}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea
                      rows={3}
                      value={block.content}
                      onChange={(e) => setScopeBlocks([{ ...block, content: e.target.value }])}
                      placeholder={t(SCOPE_PLACEHOLDER_KEYS[block.type])}
                    />

                    <div className="mt-3">
                      <ScopePhotosUploader
                        bucket="estimate-scope-photos"
                        companyId={company?.id}
                        photos={scopePhotos}
                        setPhotos={setScopePhotos}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="space-y-1.5">
              <Label>{t("pages.estimates.form.tax")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={taxPercent}
                onChange={(e) => setTaxPercent(Number(e.target.value) || 0)}
              />
            </div>
          </div>

          {/* Line items */}
          <div className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border p-4">
              <p className="text-sm font-semibold">{t("pages.estimates.form.lineItems")}</p>
              <Button variant="outline" size="sm" onClick={addItem} className="gap-2">
                <Plus className="h-3.5 w-3.5" />
                {t("pages.estimates.form.addItem")}
              </Button>
            </div>
            {/* Desktop / tablet: table view */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("pages.estimates.form.description")}</TableHead>
                    <TableHead className="w-24">{t("pages.estimates.form.qty")}</TableHead>
                    <TableHead className="w-32">{t("pages.estimates.form.unitPrice")}</TableHead>
                    <TableHead className="w-32 text-right">
                      {t("pages.estimates.form.total")}
                    </TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Input
                          value={it.description}
                          onChange={(e) => updateItem(idx, { description: e.target.value })}
                          placeholder={t("pages.estimates.form.itemPlaceholder")}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={it.quantity}
                          onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={it.unit_price}
                          onChange={(e) => updateItem(idx, { unit_price: Number(e.target.value) })}
                        />
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {formatMoney((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(idx)}
                          disabled={items.length === 1}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile: card view */}
            <div className="md:hidden divide-y divide-border">
              {items.map((it, idx) => {
                const lineTotal = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                return (
                  <div key={idx} className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">
                        {t("pages.estimates.form.lineItems")} #{idx + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 -mr-2"
                        onClick={() => removeItem(idx)}
                        disabled={items.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("pages.estimates.form.description")}</Label>
                      <Textarea
                        rows={2}
                        value={it.description}
                        onChange={(e) => updateItem(idx, { description: e.target.value })}
                        placeholder={t("pages.estimates.form.itemPlaceholder")}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("pages.estimates.form.qty")}</Label>
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={it.quantity}
                          onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("pages.estimates.form.unitPrice")}</Label>
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={it.unit_price}
                          onChange={(e) => updateItem(idx, { unit_price: Number(e.target.value) })}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
                      <span className="text-xs text-muted-foreground">
                        {t("pages.estimates.form.total")}
                      </span>
                      <span className="text-sm font-semibold">{formatMoney(lineTotal)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-end gap-1 border-t border-border p-4 text-sm">
              <div className="flex w-56 justify-between">
                <span className="text-muted-foreground">{t("pages.estimates.form.subtotal")}</span>
                <span>{formatMoney(subtotal)}</span>
              </div>
              <div className="flex w-56 justify-between">
                <span className="text-muted-foreground">
                  {t("pages.estimates.form.taxLabel", { percent: taxPercent })}
                </span>
                <span>{formatMoney(taxAmount)}</span>
              </div>
              <div className="mt-1 flex w-56 justify-between border-t border-border pt-1 text-base font-semibold">
                <span>{t("pages.estimates.form.total")}</span>
                <span>{formatMoney(total)}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => save("draft")} disabled={saving}>
              {t("pages.estimates.form.saveDraft")}
            </Button>
            <Button onClick={() => save("sent")} disabled={saving}>
              {saving ? t("pages.estimates.form.saving") : t("pages.estimates.form.saveSent")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
